import { useEffect, useState, useCallback } from "react";
import { fetchLogs } from "../../../api/customAdmin/customAdminService";
import SingleSelect from "../../../components/ui/forms/SingleSelect";
import TablaAdminLogs from "../../../components/ui/table/TablaAdminLogs";

const AdminLogsSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action_type: "",
    decision: "",
    from: "",
    to: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    ordering: "-created_at",
  });
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filters, ...pagination };
      const data = await fetchLogs(params);
      const results = Array.isArray(data?.results) ? data.results : data;
      setItems(results || []);
      if (data?.pagination) {
        setTotalPages(data.pagination.total_pages || 1);
      }
    } catch (e) {
      setError("Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  useEffect(() => {
    load();
  }, [filters, pagination, load]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updatePagination = (key, value) => {
    setPagination((prev) => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      action_type: "",
      decision: "",
      from: "",
      to: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  if (loading)
    return (
      <div className="note info">
        <b>Loading admin logs...</b>
      </div>
    );
  if (error)
    return (
      <div className="note error">
        <b>Error loading logs: {error}</b>
      </div>
    );

  return (
    <div>
      {/* Filters Section */}
      <div className="form-section admin-logs-filters">
        <div className="admin-logs-filters-header">
          <h4>Filters</h4>
          <button
            className="btn btnSecondary admin-logs-clear"
            onClick={clearAllFilters}
          >
            <b>Clear All Filters</b>
          </button>
        </div>
        <div className="columns col3 admin-logs-filters-grid">
          <div className="select-wrapper">
            <SingleSelect
              optionParam="adminActionTypes"
              name="action_type"
              defaultValue={filters.action_type}
              onChange={(value) => updateFilter("action_type", value)}
              placeholder="Filter By Action Type"
            />
          </div>
          <div className="select-wrapper">
            <SingleSelect
              optionParam="adminDecisions"
              name="decision"
              defaultValue={filters.decision}
              onChange={(value) => updateFilter("decision", value)}
              placeholder="Filter By Decision Type"
            />
          </div>
          <div className="inputText">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => updateFilter("from", e.target.value)}
            />
            <label>From Date</label>
          </div>
          <div className="inputText">
            <input
              type="date"
              value={filters.to}
              onChange={(e) => updateFilter("to", e.target.value)}
            />
            <label>To Date</label>
          </div>
          <div className="select-wrapper">
            <SingleSelect
              optionParam="adminOrdering"
              name="ordering"
              defaultValue={pagination.ordering}
              onChange={(value) => updatePagination("ordering", value)}
              placeholder="Order by"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <TablaAdminLogs items={items} />

      {/* Pagination */}
      <div className="prevNext">
        <div className="select-wrapper page-size-select">
          <label>Page Size</label>
          <SingleSelect
            optionParam="pageSizes"
            name="page_size"
            defaultValue={pagination.page_size.toString()}
            onChange={(value) => updatePagination("page_size", parseInt(value))}
          />
        </div>
        <div className="pagination-controls">
          <button
            className="btn btnSecondary"
            onClick={() =>
              updatePagination("page", Math.max(1, pagination.page - 1))
            }
            disabled={pagination.page <= 1}
          >
            <b>Previous</b>
          </button>
          <span className="page-info">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            className="btn btnSecondary"
            onClick={() =>
              updatePagination(
                "page",
                Math.min(totalPages, pagination.page + 1)
              )
            }
            disabled={pagination.page >= totalPages}
          >
            <b>Next</b>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogsSection;
